import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { InvoiceState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import InvoiceStateChip from '~components/Invoice/InvoiceState';
import SettlementChip from '~components/Invoice/SettlementChip';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import {
  payableInvoiceCategories,
  receivableInvoiceCategories,
} from '~pages/Approvals/DriverPayV2/constants';
import DriverPayPageContext from '~pages/Approvals/DriverPayV2/DriverPayPageContext';
import { getFriendlySettlementId } from '~utils/settlements/settlement-utils';

import InvoicesQueryContext from '../../OrdersList/ExpandedBlock/InvoicesQueryContext';
import { FlexColumn } from '../../styled';

interface ActionsColumnProps {
  invoice: Invoice;
  sx?: SxProps<Theme>;
}

export default function ActionsColumn({ invoice, sx }: ActionsColumnProps) {
  const { approveInvoice } = useInvoices();
  const { updateInvoice } = useContext(InvoicesQueryContext);
  const { currentCategory } = useContext(DriverPayPageContext);

  const [isBusy, setIsBusy] = useState(false);

  const { id, state, jobJobIds } = invoice;

  const isPayableApprovable = Boolean(
    payableInvoiceCategories.includes(currentCategory) &&
    [InvoiceState.PENDING, InvoiceState.CUSTOMER_PENDING].includes(state),
  );

  const isReceivableApprovable = Boolean(
    receivableInvoiceCategories.includes(currentCategory) &&
    [InvoiceState.PENDING, InvoiceState.CUSTOMER_PENDING].includes(state),
  );

  const handleApproveJobClick = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setIsBusy(true);

      const updatedInvoice = await approveInvoice(invoice);
      updateInvoice(updatedInvoice);
    } finally {
      setIsBusy(false);
    }
  }, [id, invoice]);

  let content: React.ReactNode = null;

  if (isPayableApprovable || isReceivableApprovable) {
    if (!invoice.approvable) {
      return null;
    }

    content = (
      <SmallLoadingButton
        color="success"
        data-ui={`page-approvals__driver-pay__invoices-table__row-${invoice.id}__approve-button`}
        loading={isBusy}
        onClick={handleApproveJobClick}
        sx={(theme) => ({ bgcolor: theme.brandV2.colors.treadGreen })}
      >
        {t('actions.approve')}
      </SmallLoadingButton>
    );
  }

  if (invoice.settlement) {
    content = (
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
        <Chip
          color="success"
          hideDropdownArrow
          label={t('approvals.driver_day.approved')}
        />

        <SettlementChip
          settlementId={getFriendlySettlementId(invoice.settlement, currentCategory)}
          category={currentCategory}
        />
      </Box>
    );
  }

  if (!content) {
    content = <InvoiceStateChip state={state} size="small" />;
  }

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        ...sx,
        display: 'flex',
        gap: 1,
        justifyContent: 'flex-end',
      }}
    >
      <FlexColumn>
        {content}

        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray3,
            fontSize: '10px',
            textAlign: 'right',
          })}
        >
          {jobJobIds}
        </Typography>
      </FlexColumn>
    </Box>
  );
}
